import type React from 'react';
import { useDrop } from 'react-dnd';
import FileCopy from '@mui/icons-material/FileCopy';
import FileCopyOutlined from '@mui/icons-material/FileCopyOutlined';
import { Box } from '@mui/material';

import { DRAG_ITEM_TYPES } from 'src/constants/dragItem';
import type { DragItem } from 'src/types/workspace';
import { useWorkspaceContext } from 'src/components/workspace/WorkspaceContext';

import { styles } from './index.styles';

interface BreakDropTargetProps {
  datalineRenderId: string;
  isReadOnly?: boolean;
}
const BreakDropTarget: React.FC<BreakDropTargetProps> = ({
  datalineRenderId,
  isReadOnly = false,
}) => {
  const { breakVariableIntoNewDatalines, setShowLoadingIndicator } =
    useWorkspaceContext();

  const [{ isOver, canDrop }, BreakDropTargetElement] = useDrop<
    DragItem,
    void,
    { isOver: boolean; canDrop: boolean }
  >({
    accept: [DRAG_ITEM_TYPES.VARIABLE],

    drop: (dragItem, monitor) => {
      // Do nothing if the item was dropped on a child element of the current definitionline.
      if (monitor.didDrop() || !monitor.isOver({ shallow: true }) || isReadOnly)
        return;

      setShowLoadingIndicator(true, () =>
        breakVariableIntoNewDatalines(dragItem, datalineRenderId),
      );
    },

    canDrop: () => !isReadOnly,

    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <Box
      sx={styles.breakDropTarget(canDrop, isOver)}
      ref={BreakDropTargetElement}
    >
      {canDrop ? <FileCopy /> : <FileCopyOutlined />}
    </Box>
  );
};

export default BreakDropTarget;
